import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  FormControlLabel,
  Switch,
  Typography,
  Box
} from '@mui/material';
import { toast } from 'react-toastify';
import StandardInputField from '../StandardComponents/StandardInputField/StandardInputField';
import { GetFrameworkIndustryTypes, GetFrameworkData } from '../../Api/Framework';
import { hasEssentialsTier, parseJsonArray } from '../../Utils/frameworkTiers';

export const ONBOARDING_ROUTE_OPTIONS = [
  { value: 'simplified', label: 'Simplified — /bizdetails (Business Fact Sheet)' },
  { value: 'brand', label: 'Brand — /onboarding/brand' },
  { value: 'full', label: 'Full — /onboarding (9-step onboarding)' }
];

const CODE_PATTERN = /^[A-Za-z0-9_-]{2,50}$/;
const FIELD_CONFIG = { uiStyle: 'field_styling_25' };
const NONE_FRAMEWORK = '';

const parseIndustryList = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const getFrameworkIndustryTypes = (framework) =>
  parseJsonArray(framework?.framework_industry_type);

const createInitialFormState = (data) => ({
  code: data?.code || '',
  display_name: data?.display_name || '',
  description: data?.description || '',
  onboarding_route: data?.onboarding_route || 'simplified',
  welcome_message: data?.welcome_message || '',
  logo_url: data?.logo_url || '',
  default_country: data?.default_country || '',
  allowed_industries: parseIndustryList(data?.allowed_industries),
  forced_framework_id: data?.forced_framework_id || NONE_FRAMEWORK,
  is_active: data?.is_active !== undefined ? data.is_active : true
});

const SignupCodeModal = ({ open, onClose, onSubmit, initialData = null, isSubmitting = false }) => {
  const [formState, setFormState] = useState(createInitialFormState(initialData));
  const [industryOptions, setIndustryOptions] = useState([]);
  const [essentialsFrameworks, setEssentialsFrameworks] = useState([]);

  useEffect(() => {
    if (open) {
      setFormState(createInitialFormState(initialData));
    }
  }, [open, initialData]);

  useEffect(() => {
    if (!open) return;
    GetFrameworkIndustryTypes()
      .then((response) => {
        const types = Array.isArray(response?.data) ? response.data : [];
        setIndustryOptions(types.map((type) => ({ value: type, label: type })));
      })
      .catch(() => setIndustryOptions([]));
    GetFrameworkData()
      .then((response) => {
        const list = Array.isArray(response?.data) ? response.data : [];
        setEssentialsFrameworks(list.filter(hasEssentialsTier));
      })
      .catch(() => setEssentialsFrameworks([]));
  }, [open]);

  const frameworkOptions = useMemo(() => {
    const industries = formState.allowed_industries || [];
    const filtered =
      industries.length === 0
        ? essentialsFrameworks
        : essentialsFrameworks.filter((framework) =>
            getFrameworkIndustryTypes(framework).some((type) => industries.includes(type))
          );
    const options = filtered
      .slice()
      .sort((a, b) => {
        const orderA = a.framework_order ?? 999999;
        const orderB = b.framework_order ?? 999999;
        if (orderA !== orderB) return orderA - orderB;
        return (a.name || '').localeCompare(b.name || '');
      })
      .map((framework) => ({
        value: framework.id,
        label: framework.name_acronym
          ? `${framework.name} (${framework.name_acronym})`
          : framework.name || framework.id,
      }));
    return [
      { value: NONE_FRAMEWORK, label: 'None — use framework order (single best)' },
      ...options,
    ];
  }, [essentialsFrameworks, formState.allowed_industries]);

  // Clear forced framework if industries change and it no longer matches.
  useEffect(() => {
    const forcedId = formState.forced_framework_id;
    if (!forcedId) return;
    const stillValid = frameworkOptions.some((opt) => opt.value === forcedId);
    if (!stillValid) {
      setFormState((prev) => ({ ...prev, forced_framework_id: NONE_FRAMEWORK }));
    }
  }, [frameworkOptions, formState.forced_framework_id]);

  const handleChange = (field) => (event) => {
    setFormState((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSelectChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    const code = formState.code.trim();
    if (!CODE_PATTERN.test(code)) {
      toast.error('Code must be 2-50 characters: letters, digits, hyphen or underscore.');
      return;
    }
    const country = formState.default_country.trim().toUpperCase();
    if (country && !/^[A-Z]{2}$/.test(country)) {
      toast.error('Default country must be a 2-letter code (e.g. SG, AU).');
      return;
    }
    const forcedId = formState.forced_framework_id?.trim() || '';
    onSubmit({
      code,
      display_name: formState.display_name.trim() || code,
      description: formState.description.trim() || null,
      onboarding_route: formState.onboarding_route,
      welcome_message: formState.welcome_message.trim() || null,
      logo_url: formState.logo_url.trim() || null,
      default_country: country || null,
      allowed_industries: formState.allowed_industries?.length
        ? formState.allowed_industries
        : null,
      forced_framework_id: forcedId || null,
      is_active: formState.is_active
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initialData ? 'Edit Signup Code' : 'Add Signup Code'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6}>
            <StandardInputField
              type="text"
              name="code"
              label="Code"
              required={true}
              value={formState.code}
              onChange={handleChange('code')}
              placeholder="e.g. SAIA"
              helperText="Used in the link /register/signup/<code>"
              config={FIELD_CONFIG}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StandardInputField
              type="text"
              name="display_name"
              label="Display Name"
              value={formState.display_name}
              onChange={handleChange('display_name')}
              placeholder="e.g. SAIA"
              config={FIELD_CONFIG}
            />
          </Grid>
          <Grid item xs={12}>
            <StandardInputField
              type="text"
              name="description"
              label="Description"
              value={formState.description}
              onChange={handleChange('description')}
              placeholder="e.g. SAIA partner program"
              config={FIELD_CONFIG}
            />
          </Grid>
          <Grid item xs={12}>
            <StandardInputField
              type="select"
              name="onboarding_route"
              label="Onboarding Route"
              value={formState.onboarding_route}
              onChange={handleChange('onboarding_route')}
              options={ONBOARDING_ROUTE_OPTIONS}
              helperText="Which onboarding flow signups from this link get"
              config={FIELD_CONFIG}
            />
          </Grid>
          <Grid item xs={12}>
            <StandardInputField
              type="select"
              name="allowed_industries"
              label="Allowed Industries"
              value={formState.allowed_industries || []}
              onChange={handleSelectChange}
              options={industryOptions}
              placeholder="All industries (no restriction)"
              helperText="Leave empty to allow all industries during onboarding"
              config={{
                uiStyle: 'field_styling_25',
                multiple: true,
                withCheckbox: true,
                renderValue: (selected) => {
                  if (!selected || selected.length === 0) return 'All industries (no restriction)';
                  if (selected.length === 1) return selected[0];
                  return `${selected[0]}, +${selected.length - 1} more`;
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <StandardInputField
              type="select"
              name="forced_framework_id"
              label="Force Project Framework"
              value={formState.forced_framework_id || NONE_FRAMEWORK}
              onChange={handleChange('forced_framework_id')}
              options={frameworkOptions}
              helperText="At most one project is started from onboarding; this picks which Essentials framework. Empty uses framework order (single best)."
              config={FIELD_CONFIG}
            />
          </Grid>
          <Grid item xs={12}>
            <StandardInputField
              type="multiline"
              name="welcome_message"
              label="Welcome Message"
              value={formState.welcome_message}
              onChange={handleChange('welcome_message')}
              placeholder='Shown on the signup page, e.g. "Welcome, SAIA members!"'
              config={{ ...FIELD_CONFIG, rows: 2 }}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <StandardInputField
              type="text"
              name="logo_url"
              label="Logo URL"
              value={formState.logo_url}
              onChange={handleChange('logo_url')}
              placeholder="https://... (optional partner logo)"
              config={FIELD_CONFIG}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StandardInputField
              type="text"
              name="default_country"
              label="Default Country"
              value={formState.default_country}
              onChange={handleChange('default_country')}
              placeholder="e.g. SG"
              helperText="2-letter code (optional)"
              config={FIELD_CONFIG}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formState.is_active}
                    onChange={(event) =>
                      setFormState((prev) => ({ ...prev, is_active: event.target.checked }))
                    }
                  />
                }
                label="Active"
              />
              <Typography variant="caption" sx={{ color: '#6B7280' }}>
                Inactive codes stop resolving; visitors fall back to the default signup flow.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>
          {initialData ? 'Save Changes' : 'Create Code'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SignupCodeModal;
